/**
 * Type of change
 */
export type ChangeType = 'insert' | 'update' | 'delete';

/**
 * A single cell update
 */
export interface CellChange<T = unknown> {
  rowId: string | number;
  row: T;
  columnId: string;
  oldValue: unknown;
  newValue: unknown;
  timestamp: Date;
}

/**
 * An insert operation
 */
export interface InsertChange<T = unknown> {
  tempId: string;  // Temporary ID for tracking
  row: T;
  timestamp: Date;
}

/**
 * A delete operation
 */
export interface DeleteChange<T = unknown> {
  rowId: string | number;
  row: T;
  timestamp: Date;
}

/**
 * All changes collected
 */
export interface ChangeSet<T = unknown> {
  inserts: InsertChange<T>[];
  updates: Map<string | number, CellChange<T>[]>;  // rowId -> changes
  deletes: DeleteChange<T>[];
}

/**
 * Result of saving changes
 */
export interface SaveResult {
  success: boolean;
  insertedIds?: Map<string, string | number>;  // tempId -> realId
  errors?: Map<string, string>;  // rowId:columnId -> error
  summary: {
    inserted: number;
    updated: number;
    deleted: number;
    failed: number;
  };
}

/**
 * Handler for saving changes
 */
export type SaveChangesHandler<T> = (changes: ChangeSet<T>) => Promise<SaveResult>;

/**
 * What a save would run, for the person to read before saving: one statement per
 * change, or why the changes cannot be previewed.
 */
export type ChangesPreview = { statements: string[]; note?: string; error?: string };
export type PreviewChangesHandler<T> = (changes: ChangeSet<T>) => Promise<ChangesPreview>;

/**
 * Tracks all changes (inserts, updates, deletes) for batch saving
 */
export class ChangeTracker<T = unknown> {
  private inserts: Map<string, InsertChange<T>> = new Map();
  // Staged rows by object identity (see rowReplaced): inserted row -> tempId, edited row -> original id
  private insertedRows: Map<T, string> = new Map();
  private editedRows: Map<T, string | number> = new Map();
  // The row as it was before its first staged edit, by original id: what the database holds
  private originals: Map<string | number, T> = new Map();
  private updates: Map<string | number, Map<string, CellChange<T>>> = new Map();
  private deletes: Map<string | number, DeleteChange<T>> = new Map();
  private rowIdGetter: (row: T) => string | number;
  private tempIdCounter = 0;

  constructor(rowIdGetter: (row: T) => string | number) {
    this.rowIdGetter = rowIdGetter;
  }

  /**
   * The model replaced a row object with an edited copy. Staged changes follow the
   * row by identity: an inserted row has no key yet and an edited row may have had
   * its key changed, so neither can be found again by rowIdGetter.
   */
  rowReplaced(oldRow: T, newRow: T): void {
    const tempId = this.insertedRows.get(oldRow);
    if (tempId !== undefined) {
      this.insertedRows.delete(oldRow);
      this.insertedRows.set(newRow, tempId);
      const insert = this.inserts.get(tempId);
      if (insert) insert.row = newRow;
    }
    const rowId = this.editedRows.get(oldRow);
    if (rowId !== undefined) {
      this.editedRows.delete(oldRow);
      this.editedRows.set(newRow, rowId);
    }
  }

  /** True when the row object is a staged insert. */
  isInsertedRow(row: T): boolean {
    return this.insertedRows.has(row);
  }

  /** The staged edit of one cell of an existing row, when it has one. */
  cellChange(row: T, columnId: string): CellChange<T> | undefined {
    const rowId = this.editedRows.get(row);
    return rowId === undefined ? undefined : this.updates.get(rowId)?.get(columnId);
  }

  /**
   * Track a cell update
   */
  trackUpdate(row: T, columnId: string, oldValue: unknown, newValue: unknown): void {
    // An edit to a row we inserted is part of the insert; rowReplaced carries the value.
    if (this.insertedRows.has(row)) {
      return;
    }

    // The row's id when it was first edited: an edit to a key column must not turn
    // later edits of the same row into a second row's.
    let rowId = this.editedRows.get(row);
    if (rowId === undefined) {
      rowId = this.rowIdGetter(row);
      this.editedRows.set(row, rowId);
      if (!this.originals.has(rowId)) this.originals.set(rowId, row);
    }

    // Track as update
    if (!this.updates.has(rowId)) {
      this.updates.set(rowId, new Map());
    }

    const rowUpdates = this.updates.get(rowId)!;

    // Check if we already have an update for this cell
    const existingChange = rowUpdates.get(columnId);
    if (existingChange) {
      // Update the new value, keep the original old value
      existingChange.newValue = newValue;
      existingChange.timestamp = new Date();

      // If value reverted to original, remove the change
      if (existingChange.newValue === existingChange.oldValue) {
        rowUpdates.delete(columnId);
        // If no more changes for this row, remove the row entry
        if (rowUpdates.size === 0) {
          this.updates.delete(rowId);
        }
      }
    } else {
      // Only track if the value actually changed
      if (oldValue !== newValue) {
        rowUpdates.set(columnId, {
          rowId,
          row,
          columnId,
          oldValue,
          newValue,
          timestamp: new Date(),
        });
      } else {
        // If no change and no other changes for this row, clean up
        if (rowUpdates.size === 0) {
          this.updates.delete(rowId);
        }
      }
    }
  }

  /**
   * Track an insert
   */
  trackInsert(row: T): string {
    const tempId = `temp_${++this.tempIdCounter}`;
    this.inserts.set(tempId, {
      tempId,
      row,
      timestamp: new Date(),
    });
    this.insertedRows.set(row, tempId);
    return tempId;
  }

  /**
   * Track a delete
   */
  trackDelete(row: T): string | number | undefined {
    // Deleting a row we inserted just drops the insert
    const tempId = this.insertedRows.get(row);
    if (tempId !== undefined) {
      this.insertedRows.delete(row);
      this.inserts.delete(tempId);
      return undefined;
    }

    // The id the row had before any staged edit: that is the row the database holds
    const rowId = this.editedRows.get(row) ?? this.rowIdGetter(row);
    this.editedRows.delete(row);

    // Remove any pending updates for this row
    this.updates.delete(rowId);

    // Track as delete, of the row as the database holds it
    this.deletes.set(rowId, {
      rowId,
      row: this.originals.get(rowId) ?? row,
      timestamp: new Date(),
    });
    return rowId;
  }

  /**
   * Check if there are any pending changes
   */
  hasChanges(): boolean {
    return this.inserts.size > 0 || this.updates.size > 0 || this.deletes.size > 0;
  }

  /**
   * Get count of changes
   */
  getChangeCounts(): { inserts: number; updates: number; deletes: number; total: number } {
    const inserts = this.inserts.size;
    const updates = this.updates.size;
    const deletes = this.deletes.size;
    return { inserts, updates, deletes, total: inserts + updates + deletes };
  }

  /**
   * Get all changes
   */
  getChangeSet(): ChangeSet<T> {
    const inserts = Array.from(this.inserts.values());
    const updates = new Map<string | number, CellChange<T>[]>();

    for (const [rowId, rowUpdates] of this.updates.entries()) {
      updates.set(rowId, Array.from(rowUpdates.values()));
    }

    const deletes = Array.from(this.deletes.values());

    return { inserts, updates, deletes };
  }

  /**
   * Clear all changes (after successful save)
   */
  clear(): void {
    this.inserts.clear();
    this.updates.clear();
    this.deletes.clear();
    this.insertedRows.clear();
    this.editedRows.clear();
    this.originals.clear();
  }

  /**
   * Discard changes (cancel/revert)
   */
  discard(): void {
    this.clear();
  }

  /**
   * Get changes for a specific row
   */
  getRowChanges(rowId: string | number): CellChange<T>[] | undefined {
    const changes = this.updates.get(rowId);
    return changes ? Array.from(changes.values()) : undefined;
  }

  /**
   * Check if a specific cell has changes
   */
  hasCellChange(rowId: string | number, columnId: string): boolean {
    const rowUpdates = this.updates.get(rowId);
    return rowUpdates?.has(columnId) ?? false;
  }

  /**
   * Get a specific cell change
   */
  getCellChange(rowId: string | number, columnId: string): CellChange<T> | undefined {
    return this.updates.get(rowId)?.get(columnId);
  }

  /**
   * Check if a row is marked for deletion
   */
  isDeleted(rowId: string | number): boolean {
    return this.deletes.has(rowId);
  }

  /**
   * Check if a row is newly inserted
   */
  isInserted(rowId: string | number): boolean {
    return this.inserts.has(String(rowId));
  }

  /**
   * Undo a specific cell change
   */
  undoCellChange(rowId: string | number, columnId: string): unknown | undefined {
    const rowUpdates = this.updates.get(rowId);
    if (!rowUpdates) return undefined;

    const change = rowUpdates.get(columnId);
    if (!change) return undefined;

    rowUpdates.delete(columnId);

    // If no more changes for this row, remove the row entry
    if (rowUpdates.size === 0) {
      this.updates.delete(rowId);
    }

    return change.oldValue;
  }
}
